import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FaHeart } from "react-icons/fa";
import { useDispatch } from "react-redux";
import LensType from "./LensType"; // Ensure the path is correct
import apiService from "../../services/api_services.js";
import auth_service from "../../services/auth_service.js";
import { useParams, useNavigate } from "react-router-dom";
import VirtualTryOnModal from "./VirtualTryOnModal.js";
import SimilarProducts from "./SimilarProducts.js";
import toast from "react-hot-toast";
import "primereact/resources/themes/lara-light-blue/theme.css"; // Choose your theme
import "primereact/resources/primereact.min.css"; // Core CSS
import { addToCart } from "../../redux/spectsgenieSlice";
import ImageMagnifier from "./ImageMagnifier.js";
import { useCart } from "../../context/CartContext"; // Import the useCart hook
import { useWishlist } from "../../context/WishlistContext"; // Import the useWishlist hook
import SimilarLens from "./SimilarLens.js";
import PrescriptionForm from "./PrescriptionForm";
import StarRating from "../../components/StarRating";
import RatingModal from "../../components/RatingModal";
import RecentlyViewed from "../../components/RecentlyViewed";
import { addToRecentlyViewed } from "../../utils/recentlyViewed";

const ProductDetails = () => {
  const { productId, categoryId } = useParams(); // Capture the product ID from the route parameter
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [productInfo, setProductInfo] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [isLensTypeOpen, setIsLensTypeOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [virtualTryOnModalState, setVirtualTryOnModalState] = useState(false);
  const [quantity, setQuantity] = useState(1); // New state for quantity
  const [isContactLensModalOpen, setIsContactLensModalOpen] = useState(false);
  // Virtual try-on state is now handled by virtualTryOnModalState
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [currentRating, setCurrentRating] = useState(null);
  const [productRatings, setProductRatings] = useState({
    avg_rating: '',
    rating_count: '',
    user_rating: null
  });
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);

  const { setCartCount } = useCart(); // Use the setCartCount function from CartContext
  const { isInWishlist, toggleWishlist, isLoggedIn } = useWishlist(); // Use the wishlist context
  const [customerId, setCustomerId] = useState(null);

  // Get customer ID from the access token
  useEffect(() => {
    try {
      const { id } = auth_service.getAccessTokenData();
      setCustomerId(id);
      console.log("Customer ID:", id);
    } catch (error) {
      console.log(error);
      setCustomerId(null);
    }
  }, []);

  useEffect(() => {
    const fetchFilteredProducts = async () => {
      try {
        const response = await apiService.fetchProductById(
          productId,
          categoryId
        );
        if (response.success && response.data.length > 0) {
          const productData = response.data[0];
          const images = productData.images
          ? productData.images
              .split(",")
              .map((img) => `https://newpos.spectsgenie.com/${img}`)
          : [];
          if (parseInt(categoryId) === 3) {
            setProductInfo({
              id: productData.id,
              ca_id: productData.ca_id,
              name: productData.name,
              slug: productData.slug,
              img: images[0], // Default to the first image
              images,
              description:
                productData.description || "No description available",
                selectedPrice: productData.price,
                selectedSprice: productData.sale_price,
                diameter: productData.diameter || "N/A",
              material: productData.material || "N/A",
              water_content: productData.water_content || "N/A",
              quantity_per_box: productData.quantity_per_box || "N/A",
              colors: [],
            });

            setSelectedImage(images[0] || null);

            // Add to recently viewed (for contact lenses)
            addToRecentlyViewed({
              pr_id: productData.id,
              ca_id: productData.ca_id,
              name: productData.name,
              slug: productData.slug,
              pr_price: productData.price,
              pr_sprice: productData.sale_price,
              images: images
            });
          }else {
            // Handle other categories
            const images = productData.pr_image
              ? productData.pr_image
                  .split(",")
                  .map((img) => `https://newpos.spectsgenie.com/${img}`)
              : [];

            const colors = Array.isArray(response.similarProducts)
              ? response.similarProducts.map((item) => ({
                  slug: item.slug,
                  pr_id: item.pr_id,
                  color: item.color || "",
                  frame_color: item.frame_color || "",
                }))
              : [];

            setProductInfo({
              id: productData.pr_id,
              ca_id: productData.ca_id,
              name: productData.name,
              pr_image: productData.pr_image,
              psd_files: productData.psd_files,
              slug: productData.slug,
              shape: productData.shape,
              selectedPrice: productData.pr_price,
              selectedSprice: productData.pr_sprice,
              img: images[productData.selected_image_to_show || 0], // Default to the first image
              images,
              description:
                productData.pr_description || "No description available",
              price: parseFloat(productData.pr_price) || 0,
              colors: colors,
              frameSize: productData.frame_size,
              frameStyle: productData.frame_style,
              material: productData.pr_material,
              sale_ends_time: productData.sale_ends_time,
              on_sale: productData.on_sale,
            });

            // Set rating data from API response
            setProductRatings({
              avg_rating: productData.avg_rating || 0,
              rating_count: productData.rating_count || 0,
              user_rating: productData.user_rating || null
            });

            setSelectedImage(
              images[productData.selected_image_to_show || 0] || null
            );

            // Set the initial selected color based on current product
            // Find the color that matches the current product
            const currentColor = colors.find(color => color.pr_id === parseInt(productData.pr_id));
            if (currentColor) {
              setSelectedColor(currentColor.slug);
            } else {
              // If current product is not in colors array, set to current product's slug
              setSelectedColor(productData.slug);
            }

            // Add to recently viewed
            addToRecentlyViewed(productData);
          }


        } else {
          console.warn("No product data found.");
        }
      } catch (error) {
        console.error("Error fetching filtered products:", error);
      }
    };

    fetchFilteredProducts();
  }, [productId, categoryId, isLoggedIn, customerId]);



  const handleImageChange = (image) => {
    setSelectedImage(image);
  };

  const handleQuantityChange = (increment) => {
    setQuantity((prevQuantity) => Math.max(0, prevQuantity + increment));
  };

  const allColors = {
    black: "#000",
    white: "#fff",
    brown: "#964B00",
    grey: "#808080",
    purple: "#A020F0",
    green: "#008000",
    pink: "#FFC0CB",
    blue: "#4169E1",
    red: "#ff2800",
    yellow: "#FFFF00",
  };

  const getSimilarProductIconColor = (colorObj) => {
    // First check if the product has a specific color field
    if (colorObj.color && colorObj.color !== "") {
      return colorObj.color;
    }
    
    // Check if product has frame_color field
    if (colorObj.frame_color && colorObj.frame_color !== "") {
      return colorObj.frame_color;
    }
    
    // Fallback to slug-based color detection
    if (!colorObj.slug) return "#000000";
    const colorKeys = Object.keys(allColors);
    const lowerSlug = colorObj.slug.toLowerCase();
    for (const color of colorKeys) {
      if (lowerSlug.includes(color)) {
        return allColors[color];
      }
    }
    return "#000000";
  };

  const handleWishlistToggle = async () => {
    if (!productInfo) return;
    
    setWishlistLoading(true);
    try {
      const productData = {
        product_id: productInfo.id,
        ca_id: productInfo.ca_id,
        name: productInfo.name || productInfo.product_name,
        price: productInfo.selectedSprice || productInfo.selectedPrice,
        pr_image: productInfo.pr_image,
        pr_image_json: productInfo.pr_image_json,
        slug: productInfo.slug
      };

      const result = await toggleWishlist(productData);
      
      if (result.success) {
      //   toast.success(result.message, {
      //     duration: 2000,
      //     position: 'top-center',
      //     style: {
      //       background: '#10B981',
      //       color: '#fff',
      //     }
      //   });
      } else {
        toast.error(result.message, {
          duration: 2000,
          position: 'top-center',
          style: {
            background: '#EF4444',
            color: '#fff',
          }
        });
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
      toast.error("Error updating wishlist", {
        duration: 2000,
        position: 'top-center',
        style: {
          background: '#EF4444',
          color: '#fff',
        }
      });
    } finally {
      setWishlistLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (productInfo) {
      dispatch(
        addToCart({
          _id: productInfo.id,
          name: productInfo.name,
          quantity: 1,
          image: productInfo.img,

          price: productInfo.price,
          colors: productInfo.colors,
          ca_id: productInfo.ca_id,
        })
      );
      setIsLensTypeOpen(true); // Open LensType popup after adding to cart
    }
  };

  // Add this helper function to save prescription data to DB (similar to LensType.js)
  const savePrescriptionToDB = async (prescriptionData) => {
    if (!isLoggedIn) {
      console.log("User not logged in, skipping prescription save to DB.");
      return;
    }

    const formData = new FormData();
    console.log("Prescription data to be saved:", prescriptionData);

    if (!prescriptionData) {
      console.error("Prescription data is null or undefined");
      return;
    }

    formData.append("customerId", customerId.toString());

    if (!prescriptionData.prescriptionFile) {
      formData.append(
        "rightSph",
        (prescriptionData.right?.sph || "0.00").toString()
      );
      formData.append(
        "rightCyl",
        (prescriptionData.right?.cyl || "0.00").toString()
      );
      formData.append(
        "rightAxis",
        (prescriptionData.right?.axis || "0").toString()
      );
      formData.append(
        "rightAdd",
        (prescriptionData.right?.add || "0.00").toString()
      );

      formData.append(
        "leftSph",
        (prescriptionData.left?.sph || "0.00").toString()
      );
      formData.append(
        "leftCyl",
        (prescriptionData.left?.cyl || "0.00").toString()
      );
      formData.append(
        "leftAxis",
        (prescriptionData.left?.axis || "0").toString()
      );
      formData.append(
        "leftAdd",
        (prescriptionData.left?.add || "0.00").toString()
      );

      formData.append("hasTwoPd", prescriptionData.hasTwoPD ? "1" : "0");

      if (prescriptionData.hasTwoPD) {
        formData.append("pdRight", (prescriptionData.rightPD || "").toString());
        formData.append("pdLeft", (prescriptionData.leftPD || "").toString());
        formData.append("pdSingle", "");
      } else {
        formData.append("pdSingle", (prescriptionData.pd || "").toString());
        formData.append("pdRight", "");
        formData.append("pdLeft", "");
      }
    } else {
      formData.append("rightSph", "0.00");
      formData.append("rightCyl", "0.00");
      formData.append("rightAxis", "0");
      formData.append("rightAdd", "0.00");
      formData.append("leftSph", "0.00");
      formData.append("leftCyl", "0.00");
      formData.append("leftAxis", "0");
      formData.append("leftAdd", "0.00");
      formData.append("pdSingle", "");
      formData.append("pdRight", "");
      formData.append("pdLeft", "");
      formData.append("hasTwoPd", "0");

      if (prescriptionData.prescriptionFile) {
        formData.append("prescriptionFile", prescriptionData.prescriptionFile);
      }
    }

    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }
  };

  const handleAddLensToCart = async (
    prescriptionData,
    confirmPrescription,
    uploadPrescription,
    quantity
  ) => {
    if (!isLoggedIn || !customerId) {
      const localCart = JSON.parse(localStorage.getItem("cart")) || [];
      const lensDetails = {
        product_id: productInfo.id,
        quantity: quantity,
        price: productInfo.price,
        ca_id: productInfo.ca_id,
        prescription: prescriptionData,
      };

      const existingIndex = localCart.findIndex(
        (item) =>
          item.product_id === lensDetails.product_id &&
          item.ca_id === lensDetails.ca_id
      );

      if (existingIndex !== -1) {
        localCart[existingIndex].quantity += lensDetails.quantity;
      } else {
        localCart.push(lensDetails);
      }

      localStorage.setItem("cart", JSON.stringify(localCart));
      setCartCount(localCart.length);
      // toast.success("Product added to cart", {
      //   duration: 3000,
      //   position: "top-center",
      //   style: {
      //     background: "#333",
      //     color: "#fff",
      //   },
      // });
      return;
    } else {
      console.log(productInfo);
      
      // Initialize prescription data without base64 conversion
      let updatedPrescriptionData = { ...prescriptionData };

      const payload = {
        product_id: productInfo.id,
        quantity: quantity,
        price: productInfo.selectedSprice,
        customer_id: customerId,
        ca_id: productInfo.ca_id,
        prescription: updatedPrescriptionData,
      };

      try {
        // Handle prescription file upload if present
        if (uploadPrescription && prescriptionData.prescriptionFile) {
          try {
            const fileUploadResponse = await apiService.savePrescriptionFile({
              user_id: customerId,
              file: prescriptionData.prescriptionFile
            });
            
            if (fileUploadResponse.success) {
              console.log("Prescription file saved successfully", fileUploadResponse);
              // Create a new prescription data object without the prescriptionFile property
              // but with the prescriptionFilePath from the response
              const { prescriptionFile, ...prescriptionDataWithoutFile } = updatedPrescriptionData;
              updatedPrescriptionData = {
                ...prescriptionDataWithoutFile,
                prescriptionFilePath: fileUploadResponse.data.path
              };
              
              // Update the payload with the new prescription data that includes the file path but not the file
              payload.prescription = updatedPrescriptionData;
            } else {
              console.error("Error saving prescription file:", fileUploadResponse);
              toast.error("Warning: Prescription file could not be saved", {
                duration: 3000,
                position: "top-center",
                style: {
                  background: "#ff9800",
                  color: "#fff",
                },
              });
            }
          } catch (fileError) {
            console.error("Error saving prescription file:", fileError);
            toast.error("Warning: Prescription file could not be saved", {
              duration: 3000,
              position: "top-center",
              style: {
                background: "#ff9800",
                color: "#fff",
              },
            });
          }
        }

        // Save prescription to DB (existing functionality)
        if (uploadPrescription === false) {
          // Only save prescription data to DB if manual input was used
          await savePrescriptionToDB(updatedPrescriptionData);
        }

        const response = await apiService.addContactLensToCart(payload);
        if (response.success === true) {
          const cartResponse = await apiService.fetchCartCountById(customerId);
          setCartCount(cartResponse.totalItems);
          // toast.success("Product added to cart", {
          //   duration: 3000,
          //   position: "top-center",
          //   style: {
          //     background: "#333",
          //     color: "#fff",
          //   },
          // });
        } else {
          toast.error("Failed to add product to cart", {
            duration: 3000,
            position: "top-center",
            style: {
              background: "#333",
              color: "#fff",
            },
          });
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        toast.error("Failed to add product to cart", {
          duration: 3000,
          position: "top-center",
          style: {
            background: "#333",
            color: "#fff",
          },
        });
      }
    }
  };

  const handleAddBluToCart = async () => {
    setIsAddingToCart(true);
    setAddToCartSuccess(false);
    
    try {
      if (!isLoggedIn || !customerId) {
        const localCart = JSON.parse(localStorage.getItem("cart")) || [];
        const lensDetails = {
          product_id: productInfo.id,
          quantity: quantity,
          price: productInfo.price,
          ca_id: productInfo.ca_id,
        };

        const existingIndex = localCart.findIndex(
          (item) =>
            item.product_id === lensDetails.product_id &&
            item.ca_id === lensDetails.ca_id
        );

        if (existingIndex !== -1) {
          localCart[existingIndex].quantity += lensDetails.quantity;
        } else {
          localCart.push(lensDetails);
        }

        localStorage.setItem("cart", JSON.stringify(localCart));
        setCartCount(localCart.length);
        setAddToCartSuccess(true);
        setTimeout(() => setAddToCartSuccess(false), 2000);
      } else {
        const payload = {
          product_id: productInfo.id,
          quantity: quantity,
          price: productInfo.price,
          customer_id: customerId,
          ca_id: productInfo.ca_id,
        };

        const response = await apiService.addBluToCart(payload);
        if (response.success === true) {
          const cartResponse = await apiService.fetchCartCountById(customerId);
          setCartCount(cartResponse.totalItems);
          setAddToCartSuccess(true);
          setTimeout(() => setAddToCartSuccess(false), 2000);
        } else {
          throw new Error("Failed to add to cart");
        }
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add product to cart", {
        duration: 3000,
        position: "top-center",
        style: {
          background: "#333",
          color: "#fff",
        },
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleColorClick = (colorObj) => {
    setSelectedColor(colorObj.slug);
    navigate(`/product/${colorObj.pr_id}/${productInfo.ca_id}`, {
      state: { product: colorObj },
    });
  };

  const handleRatingSubmit = async (ratingData) => {
    try {
      if (!isLoggedIn || !customerId) {
        // toast.error("Please login to rate products");
        return;
      }

      const payload = {
        product_id: productInfo.id,
        customer_id: customerId,
        rating: ratingData.rating,
        review: ratingData.review || ""
      };

      let response;
      if (ratingData.isUpdate) {
        response = await apiService.updateRating(payload);
      } else {
        response = await apiService.addRating(payload);
      }

      if (response.success) {
        // toast.success(ratingData.isUpdate ? "Rating updated successfully" : "Rating added successfully");
        // Refresh product ratings
        fetchProductRatings();
      } else {
        // toast.error("Failed to submit rating");
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      // toast.error("Failed to submit rating");
    }
  };

  const fetchProductRatings = async () => {
    try {
      if (productInfo && isLoggedIn && customerId) {
        const ratingResponse = await apiService.checkExistingRating(productInfo.id, customerId);
        if (ratingResponse.success) {
          setCurrentRating(ratingResponse.data);
        }
      }
    } catch (error) {
      console.error("Error fetching ratings:", error);
    }
  };

  // Rating functionality is handled by the RatingModal component directly

  const capitalizeWords = (str) => {
    if (!str || str === "N/A") return str;
    return str
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <div className="w-full mx-auto bg-white">
      {productInfo ? (
        <div className="max-w-7xl mx-auto px-1 py-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-2 bg-white rounded-lg shadow-md">
          {/* Main Image Section with Thumbnails Below */}
          <div className="col-span-1 md:col-span-2 xl:col-span-6 flex flex-col items-center bg-white rounded-lg overflow-hidden relative">
            <div className="relative w-full">
              <ImageMagnifier
                src={selectedImage}
                className="w-full max-h-[450px] object-contain mb-2"
                alt={productInfo.name || "Product Image"}
                magnifierHeight={300}
                magnifierWidth={300}
                zoomLevel={2}
              />
              <button
                className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-110 focus:outline-none"
                onClick={handleWishlistToggle}
                disabled={wishlistLoading}
              >
                {wishlistLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-red-500"></div>
                ) : (
                  <FaHeart 
                    className={`text-xl transition-colors duration-300 ${
                      productInfo && isInWishlist(productInfo.id) 
                        ? "text-red-500" 
                        : "text-gray-600 hover:text-red-400"
                    }`}
                    style={{
                      fill: productInfo && isInWishlist(productInfo.id) ? "#EF4444" : "transparent",
                      stroke: productInfo && isInWishlist(productInfo.id) ? "#EF4444" : "#4B5563",
                      strokeWidth: productInfo && isInWishlist(productInfo.id) ? "0" : "20"
                    }}
                  />
                )}
              </button>
              {productInfo.psd_files && productInfo.psd_files.trim() !== "" && (
              <button
  onClick={() => setVirtualTryOnModalState(true)}
  style={{
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    padding: '10px 14px',
    borderRadius: '9999px',
    backgroundColor: '#fff',
    color: '#000',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background-color 0.3s, color 0.3s',
  }}
  onMouseOver={(e) => {
    e.currentTarget.style.backgroundColor = '#000055';
    e.currentTarget.style.color = '#fff';
  }}
  onMouseOut={(e) => {
    e.currentTarget.style.backgroundColor = '#fff';
    e.currentTarget.style.color = '#000';
  }}
  title="Virtual Try On"
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: '24px', height: '24px' }}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
  Virtual Try On
</button>

              )}
            </div>
            <div className="flex flex-row justify-center gap-2 mt-2 flex-wrap">
              {productInfo.images.map((image, index) => (
                <div 
                  key={index}
                  className={`relative cursor-pointer transition-all duration-300 hover:scale-105 ${
                    selectedImage === image ? "": ""
                  }`}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index}`}
                    className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                    onClick={() => handleImageChange(image)}
                  />
                  {selectedImage === image && (
                    <div className="absolute inset-0 border-2 border-[#00007a] rounded-lg pointer-events-none"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Product Details Section */}
          <div className="col-span-1 md:col-span-2 xl:col-span-6 flex flex-col justify-start p-3 bg-white rounded-lg">
            <div className="border-b border-gray-100 pb-2">
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%'}}>
  <h1 className="text-2xl font-bold text-[#00007a] mb-1" style={{textAlign:'left', margin:0}}>
    {productInfo.name}
  </h1>
  {productInfo.sale_ends_time && (
    <SaleCountdownTimer saleEndTime={productInfo.sale_ends_time} />
  )}

  
</div>
              
              {/* Contact Lens Details */}
              {categoryId === "3" && (
                <div className="flex flex-col md:grid md:grid-cols-3 gap-2 mb-2 text-sm">
                  {productInfo.water_content && productInfo.water_content !== "N/A" && (
                    <div className="bg-blue-50 p-2 rounded w-full">
                      <div className="text-gray-500">Water Content</div>
                      <div className="font-medium text-[#00007a]">{productInfo.water_content}</div>
                    </div>
                  )}
                  {productInfo.diameter && productInfo.diameter !== "N/A" && (
                    <div className="bg-green-50 p-2 rounded w-full">
                      <div className="text-gray-500">Diameter</div>
                      <div className="font-medium text-[#00007a]">{productInfo.diameter} mm</div>
                    </div>
                  )}
                  {productInfo.material && productInfo.material !== "N/A" && (
                    <div className="bg-purple-50 p-2 rounded w-full">
                      <div className="text-gray-500">Material</div>
                      <div className="font-medium text-[#00007a]">{productInfo.material}</div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Rating Display */}
              <div className="flex items-center gap-4 mb-2">
                <div className="flex items-center gap-2">
                  <StarRating 
                    rating={productRatings.avg_rating || 0} 
                    readonly={true} 
                    size={16}
                  />
                  <span className="text-sm text-gray-600">
                    ({productRatings.rating_count || 0} reviews)
                  </span>
                </div>
               
              </div>

              <p className="text-sm text-gray-600">
                {productInfo.description}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {productInfo.slug}
              </p>
            </div>
            
            
             {/* {productInfo.shape && (
  <div className="flex flex-wrap gap-3 my-3">
    <button className="text-[#00007a] bg-white border-2 border-[#00007a] px-4 py-2 rounded-full font-semibold hover:bg-[#00007a] hover:text-white transition-colors duration-300">
      {productInfo.shape}
    </button>
  </div>
)} */}
            
            
            <div className="flex items-center gap-2 my-3">
              {productInfo.selectedPrice &&
              productInfo.selectedPrice !== productInfo.selectedSprice ? (
                <>
                  <span className="text-3xl font-bold text-[#00007a]">
                    ₹{productInfo.selectedPrice}
                  </span>
                  <span className="text-xl text-gray-500 line-through ml-2">
                    ₹{productInfo.selectedSprice}
                  </span>
                  <span className="ml-2 bg-[#FFD700] text-[#00007a] text-sm font-bold px-2 py-1 rounded-md">
                    {Math.round(((productInfo.selectedSprice - productInfo.selectedPrice) / productInfo.selectedSprice) * 100)}% OFF
                  </span>
                </>
              ) : (
                <span className="text-3xl font-bold text-[#00007a]">
                  ₹{productInfo.selectedSprice}
                </span>
              )}
            </div>
            
            {productInfo.colors && productInfo.colors.length > 0 && (
              <div className="my-4">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Available Colors</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  {productInfo.colors.map((colorObj, index) => (
                    <div
                      key={index}
                      className={`relative flex items-center justify-center w-10 h-10 rounded-full cursor-pointer transition-transform duration-300 hover:scale-110 ${
                        selectedColor === colorObj.slug
                          ? "ring-2 ring-offset-2 ring-[#00007a]"
                          : ""
                      }`}
                      onClick={() => handleColorClick(colorObj)}
                    >
                      <div
                        className="w-8 h-8 rounded-full border border-gray-300"
                        style={{
                          backgroundColor: getSimilarProductIconColor(colorObj),
                        }}
                      ></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Button Rendering Logic */}
            {categoryId === "3" ? (
              <div className="mt-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-gray-700">Quantity:</span>
                  <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      className="px-3 py-1 bg-gray-100 text-[#00007a] font-bold text-lg hover:bg-gray-200 transition-colors"
                      onClick={() => handleQuantityChange(-1)}
                    >
                      -
                    </button>
                    <span className="px-4 py-1 font-semibold">{quantity}</span>
                    <button
                      className="px-3 py-1 bg-gray-100 text-[#00007a] font-bold text-lg hover:bg-gray-200 transition-colors"
                      onClick={() => handleQuantityChange(1)}
                    >
                      +
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setIsContactLensModalOpen(true)}
                  className="w-full py-3 mt-2 bg-[#00007a] text-white rounded-lg font-semibold text-base hover:bg-[#000055] transition-colors duration-300 shadow-md hover:shadow-lg"
                >
                  Add to Cart
                </button>
              </div>
            ) : productInfo.ca_id === 2 ? (
              <div className="flex flex-col gap-2 mt-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleAddToCart}
                    className="py-2 bg-[#00007a] hover:bg-[#000055] transition duration-300 text-white text-sm rounded-lg shadow-md hover:shadow-lg font-semibold"
                  >
                    Add Power
                  </button>
                  <button
                    onClick={handleAddBluToCart}
                    disabled={isAddingToCart}
                    className={`py-2 ${
                      addToCartSuccess 
                        ? 'bg-green-600' 
                        : 'bg-[#A86523] hover:bg-[#E9A319]'  // Yellowish golden color
                    } transition duration-300 text-white text-sm rounded-lg shadow-md hover:shadow-lg font-semibold flex items-center justify-center gap-2 min-w-[120px]`}
                  >
                    {isAddingToCart ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adding...
                      </>
                    ) : addToCartSuccess ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Added!
                      </>
                    ) : (
                      'Add To Cart'
                    )}
                  </button>
                </div>
              </div>
            ) : categoryId === "12" ? (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  onClick={handleAddBluToCart}
                  disabled={isAddingToCart}
                  className={`py-2 ${
                    addToCartSuccess 
                      ? 'bg-green-600' 
                      : 'bg-[#00007a] hover:bg-[#000055]'
                  } transition duration-300 text-white text-sm rounded-lg shadow-md hover:shadow-lg font-semibold flex items-center justify-center gap-2 min-w-[120px]`}
                >
                  {isAddingToCart ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding...
                    </>
                  ) : addToCartSuccess ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Added!
                    </>
                  ) : (
                    'Add To Cart'
                  )}
                </button>
               
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  onClick={handleAddToCart}
                  className="py-2 bg-[#00007a] hover:bg-[#000055] transition duration-300 text-white text-sm rounded-lg shadow-md hover:shadow-lg font-semibold"
                >
                  Add Power
                </button>
              
              </div>
            )}
{/*             
            <div className="mt-3 p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-1 mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00007a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 text-xs">Free shipping on orders above ₹999</span>
              </div>
              <div className="flex items-center gap-1 mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00007a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 text-xs">Easy 14 days return & exchange</span>
              </div>
              <div className="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00007a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 text-xs">1 year warranty on manufacturing defects</span>
              </div>
            </div> */}
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#00007a]"></div>
        </div>
      )}

      {/* Product Specifications Section */}
      {productInfo && productInfo.ca_id !== 3 && (
        <div className="max-w-7xl mx-auto px-2 py-4 mt-4">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-[#00007a] text-white py-2 px-3">
              <h2 className="text-lg font-bold">Product Specifications</h2>
            </div>
            <div className="p-3 grid grid-cols-2 gap-3">
              <div className="border-b border-gray-200 pb-2">
  <h3 className="text-sm font-semibold text-[#00007a] mb-1">Frame Shape</h3>
  <p className="text-gray-700 text-xs">{capitalizeWords(productInfo.shape) || "N/A"}</p>
</div>
<div className="border-b border-gray-200 pb-2">
  <h3 className="text-sm font-semibold text-[#00007a] mb-1">Frame Size</h3>
  <p className="text-gray-700 text-xs">{capitalizeWords(productInfo.frameSize) || "N/A"}</p>
</div>
<div className="border-b border-gray-200 pb-2">
  <h3 className="text-sm font-semibold text-[#00007a] mb-1">Frame Material</h3>
  <p className="text-gray-700 text-xs">{capitalizeWords(productInfo.material) || "N/A"}</p>
</div>
<div className="border-b border-gray-200 pb-2">
  <h3 className="text-sm font-semibold text-[#00007a] mb-1">Frame Style</h3>
  <p className="text-gray-700 text-xs">{capitalizeWords(productInfo.frameStyle) || "N/A"}</p>
</div>
            </div>
          </div>
        </div>
      )}

      {/* Similar Products Section */}
      {productInfo && productInfo.ca_id !== 3 ? (
        <div className="max-w-7xl mx-auto px-2 py-4 mt-2">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-[#00007a] to-[#000055] text-white py-2 px-3 flex justify-between items-center">
              <h2 className="text-lg font-bold">You May Also Like</h2>
              <div className="bg-[#FFD700] text-[#00007a] px-2 py-0.5 rounded-full text-xs font-bold">Similar Style</div>
            </div>
            <div className="p-3">
              <SimilarProducts
                ca_id={productInfo.ca_id}
                pr_id={productInfo.id}
                shape={productInfo.shape}
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Similar Lens Section */}
      {productInfo && productInfo.ca_id === 3 ? (
        <div className="max-w-7xl mx-auto px-2 py-4 mt-2">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-[#00007a] to-[#000055] text-white py-2 px-3 flex justify-between items-center">
              <h2 className="text-lg font-bold">Similar Contact Lenses</h2>
              <div className="bg-[#FFD700] text-[#00007a] px-2 py-0.5 rounded-full text-xs font-bold">Recommended</div>
            </div>
            <div className="p-3">
              <SimilarLens ca_id={productInfo.ca_id} pr_id={productInfo.id} />
            </div>
          </div>
        </div>
      ) : null}

      {/* Recently Viewed Section */}
      <div className="max-w-7xl mx-auto px-2 py-4 mt-2">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-[#00007a] to-[#000055] text-white py-2 px-3 flex justify-between items-center">
            <h2 className="text-lg font-bold">Recently Viewed</h2>
            <div className="bg-[#FFD700] text-[#00007a] px-2 py-0.5 rounded-full text-xs font-bold">Your History</div>
          </div>
          <div className="p-3">
            <RecentlyViewed currentProductId={productInfo?.id} />
          </div>
        </div>
      </div>

      {/* Virtual Try On Modal */}
      {virtualTryOnModalState && (
        <VirtualTryOnModal
          setModalState={setVirtualTryOnModalState}
          pr_images={productInfo.pr_image}
          psd_files={productInfo.psd_files}
          productInfo={productInfo}
        />
      )}

      {/* Contact Lens Prescription Modal */}
      {isContactLensModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Add Prescription for Contact Lenses</h2>
              <button
                onClick={() => setIsContactLensModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <PrescriptionForm
                isSingleVision={true}
                handleAddToCart={(prescriptionData, confirmPrescription, uploadPrescription) => {
                  handleAddLensToCart(prescriptionData, confirmPrescription, uploadPrescription, quantity);
                  setIsContactLensModalOpen(false);
                }}
                totalPrice={productInfo.selectedSprice}
                ca_id={productInfo.ca_id}
              />
            </div>
          </div>
        </div>
      )}

      {/* Lens Type Modal */}
      {isLensTypeOpen && (
        <LensType
          isOpen={isLensTypeOpen}
          onClose={() => setIsLensTypeOpen(false)}
          productInfo={productInfo}
          onApiResponse={(response) => {
            console.log("API Response received in Wishlist.js:", response);
            if (response.success) {
              // toast.success("Product added to cart", {
              //   duration: 3000,
              //   position: "top-center",
              //   style: {
              //     background: "#333",
              //     color: "#fff",
              //   },
              // });
            } else {
              // toast.error("Failed to add product to cart", {
              //   duration: 3000,
              //   position: "top-center",
              //   style: {
              //     background: "#333",
              //     color: "#fff",
              //   },
              // });
            }
          }}
        />
      )}

      {/* Rating Modal */}
      {isRatingModalOpen && (
        <RatingModal
          isOpen={isRatingModalOpen}
          onClose={() => setIsRatingModalOpen(false)}
          onSubmit={handleRatingSubmit}
          product={productInfo}
          existingRating={currentRating}
        />
      )}
    </div>
  );
};

// Countdown Timer Component (copied from prodList.js for details page)
const SaleTimerContainer = styled.div`
  display: inline-flex;
  align-items: center;
  background: none;
  color: #FF4444;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 700;
  margin-left: 8px;
  white-space: nowrap;
`;
const TimerText = styled.span`
  font-size: 0.75rem;
  margin-right: 3px;
  color: #FF4444;
`;
const TimerNumbers = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  font-size: 0.7rem;
`;
const TimerUnit = styled.span`
  display: flex;
  align-items: center;
  gap: 1px;
`;
const TimerValue = styled.span`
  font-size: 0.7rem;
  line-height: 1;
`;
const TimerLabel = styled.span`
  font-size: 0.5rem;
  opacity: 0.9;
  text-transform: lowercase;
`;
const SaleEndedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
`;
const SaleCountdownTimer = ({ saleEndTime }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false
  });
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const endTime = new Date(saleEndTime).getTime();
      const difference = endTime - now;
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        setTimeLeft({ days, hours, minutes, seconds, isExpired: false });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [saleEndTime]);
  if (timeLeft.isExpired) {
    // return (
    //   //<SaleTimerContainer>
    //     //<SaleEndedBadge>Sale Ended</SaleEndedBadge>
    //   //</SaleTimerContainer>
    // );
  }
  return (
    <SaleTimerContainer>
      <TimerText>Ends in</TimerText>
      <TimerNumbers>
        {timeLeft.days > 0 && (<>
          <TimerUnit><TimerValue>{String(timeLeft.days).padStart(2, '0')}</TimerValue><TimerLabel>d</TimerLabel></TimerUnit>
          <span>:</span>
        </>)}
        <TimerUnit><TimerValue>{String(timeLeft.hours).padStart(2, '0')}</TimerValue><TimerLabel>h</TimerLabel></TimerUnit>
        <span>:</span>
        <TimerUnit><TimerValue>{String(timeLeft.minutes).padStart(2, '0')}</TimerValue><TimerLabel>m</TimerLabel></TimerUnit>
        <span>:</span>
        <TimerUnit><TimerValue>{String(timeLeft.seconds).padStart(2, '0')}</TimerValue><TimerLabel>s</TimerLabel></TimerUnit>
      </TimerNumbers>
    </SaleTimerContainer>
  );
};

export default ProductDetails;
